import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import api, { handleApi } from "pages/api/api";

import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const diasAlerta = 90;

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("pt-PT");
};

const fmtNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const isEventoNoIntervaloAlerta = (dataEvento, dias) => {
  if (!dataEvento) return false;

  const dt = new Date(dataEvento);
  if (Number.isNaN(dt.getTime())) return false;

  const hoje = startOfDay(new Date());
  const ev = startOfDay(dt);

  const diffMs = ev.getTime() - hoje.getTime();
  const diffDias = Math.round(diffMs / (1000 * 60 * 60 * 24));

  return diffDias <= Number(dias);
};

const getUrgenciaEvento = (dataEvento) => {
  if (!dataEvento) return null;

  const dt = new Date(dataEvento);
  if (Number.isNaN(dt.getTime())) return null;

  const hoje = startOfDay(new Date());
  const ev = startOfDay(dt);

  const diffMs = ev.getTime() - hoje.getTime();
  const diffDias = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDias <= 0) return "critico";
  if (diffDias <= 7) return "proximo";
  return null;
};

export default function EquipamentosEventosAlertasPopper({
  tooltip = "Alertas de eventos (equipamentos)",
  iconColor = "inherit",
}) {
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const timerRef = useRef(null);

  const carregar = async () => {
    setLoading(true);

    const [payload, err] = await handleApi(
      api.post("Equipamentos/SelEquipamentosEventosAlertasNavbar", { dias: Number(diasAlerta) })
    );

    setLoading(false);
    if (err) {
      setItems([]);
      return;
    }

    const arr = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];

    const arrFiltrado = arr.filter((it) => isEventoNoIntervaloAlerta(it?.data_evento, diasAlerta));

    // console.log("[AlertasNavbar] payload =", payload);
    // console.log("[AlertasNavbar] arr =", arr);
    // console.log("[AlertasNavbar] arrFiltrado =", arrFiltrado);

    setItems(arrFiltrado);
  };

  // badge refresca sozinho (para ficar sempre atualizado na navbar)
  useEffect(() => {
    carregar();
    timerRef.current = setInterval(carregar, 60 * 1000); // 60s (UI)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const count = items?.length || 0;

  // console.log("[AlertasNavbar] items =", items, "count =", count);

  const handleToggle = (e) => {
    if (open) setAnchorEl(null);
    else setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleOpenEvento = (it) => {
    const idEquipamento = Number(it?.id_equipamento ?? it?.idEquipamento ?? 0);

    // tenta vários nomes possíveis para o id do evento
    const idEvento = Number(
      it?.id_evento ?? it?.idEquipamentoEvento ?? it?.id_equipamento_evento ?? it?.id ?? 0
    );

    console.log("[ALERTA CLICK]", { idEquipamento, idEvento, it });

    navigate("/equipamentos/equipamentos", {
      state: {
        openEquipamentoId: idEquipamento,
        openEventoId: idEvento,
      },
    });

    handleClose();
  };

  const titulo = useMemo(() => {
    if (!count) return "Sem alertas";
    return `Alertas (${count})`;
  }, [count]);

  return (
    <>
      <Tooltip title={tooltip} arrow>
        <IconButton size="small" onClick={handleToggle} sx={{ color: iconColor }}>
          <Badge
            badgeContent={count}
            color="warning"
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 11,
                height: 18,
                minWidth: 18,
                backgroundColor:
                  count > 0 &&
                  (items || []).some((it) => getUrgenciaEvento(it?.data_evento) === "critico")
                    ? "#d32f2f"
                    : count > 0 &&
                      (items || []).some((it) => getUrgenciaEvento(it?.data_evento) === "proximo")
                    ? "#f57c00"
                    : "#fbc02d",
                color: "#000",
              },
            }}
          >
            <Icon>build</Icon>
          </Badge>
        </IconButton>
      </Tooltip>

      <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{ zIndex: 1600 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={10}
            sx={{
              width: 420,
              maxWidth: "90vw",
              borderRadius: 2,
              overflow: "hidden",
              mt: 1,
            }}
          >
            <MDBox
              px={2}
              py={1.5}
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <MDBox display="flex" flexDirection="column" lineHeight={1.2}>
                <MDTypography variant="button" fontWeight="bold">
                  {titulo}
                </MDTypography>

                <MDTypography variant="caption" sx={{ opacity: 0.7, mt: 0.25 }}>
                  Eventos vencidos, de hoje, ou que ocorrerão nos próximos {diasAlerta} dias.
                </MDTypography>
              </MDBox>

              {loading ? (
                <CircularProgress size={16} />
              ) : (
                <MDTypography variant="caption" sx={{ opacity: 0.7, mt: 0.25 }}>
                  {/* {new Date().toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })} */}
                </MDTypography>
              )}
            </MDBox>

            <Divider />

            <MDBox sx={{ maxHeight: 360, overflow: "auto" }}>
              {!items?.length ? (
                <MDBox p={2}>
                  <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                    Não há eventos pendentes no intervalo ({diasAlerta} dias) nem atrasados.
                  </MDTypography>
                </MDBox>
              ) : (
                items.map((it) => (
                  <MDBox
                    key={it.id}
                    px={2}
                    py={1.25}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                    }}
                    onClick={() => handleOpenEvento(it)}
                  >
                    <MDTypography variant="button" fontWeight="bold">
                      {it?.equipamento || "Equipamento"}{" "}
                      {it?.matricula ? `— ${String(it.matricula).toUpperCase()}` : ""}
                    </MDTypography>

                    <MDTypography variant="caption" sx={{ display: "block", opacity: 0.85 }}>
                      {it?.tipo_evento ? `${it.tipo_evento} — ` : ""}
                      {it?.titulo || `Evento #${it.id}`}
                    </MDTypography>

                    <MDBox mt={0.5} display="flex" gap={2} flexWrap="wrap">
                      <MDTypography
                        variant="caption"
                        sx={{
                          opacity: 0.75,
                          color:
                            getUrgenciaEvento(it?.data_evento) === "critico"
                              ? "error.main"
                              : getUrgenciaEvento(it?.data_evento) === "proximo"
                              ? "warning.main"
                              : "inherit",
                          fontWeight: getUrgenciaEvento(it?.data_evento) ? "bold" : "normal",
                        }}
                      >
                        Data Agend.: {fmtDate(it?.data_evento) || "—"}
                        {it?.data_limite ? ` | Limite: ${fmtDate(it.data_limite)}` : ""}
                      </MDTypography>

                      {/* <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
                        Data Realiz: {fmtDate(it?.data_evento) || "—"}
                      </MDTypography> */}

                      {it?.kms_agendamento !== null && it?.kms_agendamento !== undefined ? (
                        <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
                          KMs Agend.: {fmtNum(it?.kms_agendamento)}
                        </MDTypography>
                      ) : null}

                      <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
                        Estado: {it?.estado || "—"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                ))
              )}
            </MDBox>

            <Divider />

            <MDBox px={2} py={1.25}>
              <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                Clique num alerta para abrir o evento e tratar.
              </MDTypography>
            </MDBox>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
